import React from "react";

import {
  AddRounded,
  ArrowForwardRounded,
  DataObjectRounded,
  EditNoteRounded,
  InsertInvitationRounded,
  LocationOffRounded,
  LocationOnRounded,
  MenuRounded,
  RefreshRounded,
} from "@mui/icons-material";
import { Box, Chip, Divider, IconButton, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import useFirebase from "../../../config/context/firebaseContext";
import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { Dev } from "../../../config/util/consoleUtil";

import MainSolidButton from "../../../components/MainSolidButton";
import PageHeader from "../../../components/PageHeader";
import ModalWriteRule from "./components/ModalWriteRule";

import { DateTime } from "luxon";
import MainText from "../../../components/MainText";
import { CustomBox } from "../../../components/StyledComponents";
import { BusSchedulesType, ScheduleRuleInfoType, ScheduleRulesType } from "../../../types/bus";

export default function AdminBusRulePage() {
  const navigate = useNavigate();

  const { getConfig, forceRefreshConfigs } = useFirebase();
  const { setShowSideMenuDrawer } = useMainState();
  const { pushAlert } = useModal();

  const [schedules, setSchedules] = React.useState<BusSchedulesType>({});

  const [loading, setLoading] = React.useState(false);

  const [showModalWrite, setShowModalWrite] = React.useState<{
    id?: string;
    rule?: ScheduleRuleInfoType;
  } | null>(null);

  const [allRules, setAllRules] = React.useState<ScheduleRulesType>({});

  const getAllRules = async () => {
    try {
      const configRules = getConfig("bus_rules") as ScheduleRulesType;

      const configRulesSchedules = getConfig("bus_schedules") as BusSchedulesType;

      setSchedules(configRulesSchedules);
      setAllRules(configRules);
    } catch (error: any) {
      console.log(...new Dev("getAllRules", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter banners arquivados",
      });
    }
  };

  const handleReload = async () => {
    setLoading(true);
    await forceRefreshConfigs();
    await getAllRules();
    setLoading(false);
  };

  const onCloseModalHandler = (reload?: boolean) => {
    setShowModalWrite(null);
    if (reload) handleReload();
  };

  React.useEffect(() => {
    handleReload();
  }, []);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          title="Regras"
          leftIcon={<MenuRounded />}
          onLeftAction={() => setShowSideMenuDrawer(true)}
          rightIcon={<RefreshRounded />}
          onRightAction={handleReload}
        />

        {/* Modal */}

        {!!showModalWrite && (
          <ModalWriteRule
            onClose={onCloseModalHandler}
            id={showModalWrite?.id}
            rule={showModalWrite?.rule}
            schedules={schedules}
          />
        )}

        {/* Banners */}

        <Box mt={4} mb={2}>
          <MainSolidButton
            icon={<AddRounded />}
            text="Criar Regra"
            onClick={() => setShowModalWrite({})}
          />
        </Box>

        <Box>
          {loading ? (
            [1, 2, 3].map((item) => (
              <Skeleton
                key={item}
                animation="wave"
                variant="rounded"
                width={"100%"}
                height={100}
                sx={{ mb: 2, borderRadius: "15px" }}
              />
            ))
          ) : (
            <>
              {Object.keys(allRules).map((ruleKey) => {
                const now = new Date().getTime();
                const dateFrom = allRules[ruleKey].period?.from || now;
                const dateTo = allRules[ruleKey].period?.to || now;
                const active = now < dateTo && now > dateFrom;
                return (
                  <CustomBox key={ruleKey} sx={{ opacity: active ? 1 : 0.8 }}>
                    <MainText center>{allRules[ruleKey].name}</MainText>
                    <Box mt={1} mb={1} display={"flex"} justifyContent={"space-between"}>
                      <Box gap={1} display={"flex"} flexDirection={"column"}>
                        <Box gap={1} display={"flex"} flexWrap={"wrap"}>
                          <Chip icon={<DataObjectRounded />} label={ruleKey} />
                          <Chip
                            icon={active ? <LocationOnRounded /> : <LocationOffRounded />}
                            label={active ? "Ativo" : "Inativo"}
                          />
                        </Box>

                        <Chip
                          icon={<InsertInvitationRounded />}
                          label={
                            <Box
                              display={"flex"}
                              justifyContent={"center"}
                              alignItems={"center"}
                              gap={1}
                            >
                              {DateTime.fromMillis(dateFrom || now).toFormat("dd/LL/y")}{" "}
                              <ArrowForwardRounded fontSize="small" />{" "}
                              {DateTime.fromMillis(dateTo || now).toFormat("dd/LL/y")}
                            </Box>
                          }
                        />
                      </Box>

                      <IconButton
                        onClick={() => setShowModalWrite({ id: ruleKey, rule: allRules[ruleKey] })}
                      >
                        <EditNoteRounded />
                      </IconButton>
                    </Box>

                    <Box py={2}>
                      <Divider />
                    </Box>

                    <Box display={"flex"} gap={1} flexDirection={"column"}>
                      <CustomBox sx={{ marginBottom: 0 }}>
                        <MainText size="caption" center>
                          Domingo:
                        </MainText>
                        {allRules[ruleKey].days[0].map((day) => (
                          <MainText size="caption" center color="regular" key={day}>
                            {day}
                          </MainText>
                        ))}
                      </CustomBox>
                      <CustomBox sx={{ marginBottom: 0 }}>
                        <MainText size="caption" center>
                          Segunda:
                        </MainText>
                        {allRules[ruleKey].days[1].map((day) => (
                          <MainText size="caption" center color="regular" key={day}>
                            {day}
                          </MainText>
                        ))}
                      </CustomBox>
                      <CustomBox sx={{ marginBottom: 0 }}>
                        <MainText size="caption" center>
                          Terça:
                        </MainText>
                        {allRules[ruleKey].days[2].map((day) => (
                          <MainText size="caption" center color="regular" key={day}>
                            {day}
                          </MainText>
                        ))}
                      </CustomBox>
                      <CustomBox sx={{ marginBottom: 0 }}>
                        <MainText size="caption" center>
                          Quarta:
                        </MainText>
                        {allRules[ruleKey].days[3].map((day) => (
                          <MainText size="caption" center color="regular" key={day}>
                            {day}
                          </MainText>
                        ))}
                      </CustomBox>
                      <CustomBox sx={{ marginBottom: 0 }}>
                        <MainText size="caption" center>
                          Quinta:
                        </MainText>
                        {allRules[ruleKey].days[4].map((day) => (
                          <MainText size="caption" center color="regular" key={day}>
                            {day}
                          </MainText>
                        ))}
                      </CustomBox>
                      <CustomBox sx={{ marginBottom: 0 }}>
                        <MainText size="caption" center>
                          Sexta:
                        </MainText>
                        {allRules[ruleKey].days[5].map((day) => (
                          <MainText size="caption" center color="regular" key={day}>
                            {day}
                          </MainText>
                        ))}
                      </CustomBox>
                      <CustomBox sx={{ marginBottom: 0 }}>
                        <MainText size="caption" center>
                          Sábado:
                        </MainText>
                        {allRules[ruleKey].days[6].map((day) => (
                          <MainText size="caption" center color="regular" key={day}>
                            {day}
                          </MainText>
                        ))}
                      </CustomBox>
                    </Box>
                  </CustomBox>
                );
              })}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
